import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Paintbrush, Palette, Code2, Type, Layers, Square } from 'lucide-react';
import { GlassCard } from '@/components/glass';
import { PageHeader } from '@/components/ui';
import {
  ColorControl,
  SliderControl,
  SelectControl,
  SectionHeader,
  PreviewBox,
  ControlsPanel,
  ResetButton,
} from './design-system/DesignControls';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ColorToken {
  name: string;
  value: string;
  editable: boolean;
}

interface ColorCategory {
  label: string;
  tokens: ColorToken[];
}

interface ShadowState {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
  opacity: number;
}

interface BorderState {
  width: number;
  color: string;
  style: string;
  radius: number;
}

interface TypographyState {
  fontFamily: string;
  headingSizes: number[];
  fontWeight: number;
  lineHeight: number;
  letterSpacing: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** All editable hex/rgba tokens from ADMIN_COLORS */
const EDITABLE_COLOR_DEFAULTS: Record<string, string> = {
  forest: ADMIN_COLORS.forest,
  forestSoft: ADMIN_COLORS.forestSoft,
  forestText: ADMIN_COLORS.forestText,
  leaf: ADMIN_COLORS.leaf,
  leafSoft: ADMIN_COLORS.leafSoft,
  leafText: ADMIN_COLORS.leafText,
  navy: ADMIN_COLORS.navy,
  surface: ADMIN_COLORS.surface,
  ice: ADMIN_COLORS.ice,
  border: ADMIN_COLORS.border,
  borderMedium: ADMIN_COLORS.borderMedium,
  signal: ADMIN_COLORS.signal,
  success: ADMIN_COLORS.success,
  successBg: ADMIN_COLORS.successBg,
  successText: ADMIN_COLORS.successText,
  warning: ADMIN_COLORS.warning,
  warningBg: ADMIN_COLORS.warningBg,
  warningText: ADMIN_COLORS.warningText,
  dangerBg: ADMIN_COLORS.dangerBg,
  dangerText: ADMIN_COLORS.dangerText,
  infoBg: ADMIN_COLORS.infoBg,
  infoText: ADMIN_COLORS.infoText,
};

const COLOR_CATEGORIES: ColorCategory[] = [
  {
    label: 'Primary',
    tokens: [
      { name: 'primary', value: ADMIN_COLORS.primary, editable: false },
      { name: 'primarySoft', value: ADMIN_COLORS.primarySoft, editable: false },
      { name: 'primaryText', value: ADMIN_COLORS.primaryText, editable: false },
    ],
  },
  {
    label: 'Forest / Leaf',
    tokens: [
      { name: 'forest', value: ADMIN_COLORS.forest, editable: true },
      { name: 'forestSoft', value: ADMIN_COLORS.forestSoft, editable: true },
      { name: 'forestText', value: ADMIN_COLORS.forestText, editable: true },
      { name: 'leaf', value: ADMIN_COLORS.leaf, editable: true },
      { name: 'leafSoft', value: ADMIN_COLORS.leafSoft, editable: true },
      { name: 'leafText', value: ADMIN_COLORS.leafText, editable: true },
    ],
  },
  {
    label: 'Text',
    tokens: [
      { name: 'navy', value: ADMIN_COLORS.navy, editable: true },
      { name: 'textSecondary', value: ADMIN_COLORS.textSecondary, editable: false },
      { name: 'textTertiary', value: ADMIN_COLORS.textTertiary, editable: false },
    ],
  },
  {
    label: 'Surfaces',
    tokens: [
      { name: 'surface', value: ADMIN_COLORS.surface, editable: true },
      { name: 'ice', value: ADMIN_COLORS.ice, editable: true },
    ],
  },
  {
    label: 'Borders',
    tokens: [
      { name: 'border', value: ADMIN_COLORS.border, editable: true },
      { name: 'borderMedium', value: ADMIN_COLORS.borderMedium, editable: true },
    ],
  },
  {
    label: 'Semantic',
    tokens: [
      { name: 'signal', value: ADMIN_COLORS.signal, editable: true },
      { name: 'success', value: ADMIN_COLORS.success, editable: true },
      { name: 'successBg', value: ADMIN_COLORS.successBg, editable: true },
      { name: 'successText', value: ADMIN_COLORS.successText, editable: true },
      { name: 'warning', value: ADMIN_COLORS.warning, editable: true },
      { name: 'warningBg', value: ADMIN_COLORS.warningBg, editable: true },
      { name: 'warningText', value: ADMIN_COLORS.warningText, editable: true },
      { name: 'dangerBg', value: ADMIN_COLORS.dangerBg, editable: true },
      { name: 'dangerText', value: ADMIN_COLORS.dangerText, editable: true },
      { name: 'infoBg', value: ADMIN_COLORS.infoBg, editable: true },
      { name: 'infoText', value: ADMIN_COLORS.infoText, editable: true },
    ],
  },
];

const CSS_VAR_GROUPS = [
  { prefix: '--color-primary', steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { prefix: '--color-accent', steps: [50, 100, 200, 300, 400, 500] },
  { prefix: '--color-neutral', steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
];

const HEADING_LEVELS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
const DEFAULT_HEADING_SIZES = [36, 30, 24, 20, 16, 14];

const FONT_FAMILY_OPTIONS = [
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'System UI', value: 'system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Monospace', value: 'ui-monospace, monospace' },
];

const DEFAULT_SHADOW_STATES: Record<string, ShadowState> = {
  shadow: { x: 0, y: 4, blur: 16, spread: 0, color: '#1565c0', opacity: 0.08 },
  shadowMd: { x: 0, y: 8, blur: 24, spread: 0, color: '#1565c0', opacity: 0.1 },
  shadowLg: { x: 0, y: 12, blur: 32, spread: 0, color: '#1565c0', opacity: 0.12 },
};

const BORDER_STYLE_OPTIONS = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getCssVarValue(varName: string): string {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || '(unset)';
}

/** Convert a hex color to a hex string safe for <input type="color"> */
function toHexInput(value: string): string {
  if (value.startsWith('#') && (value.length === 7 || value.length === 4)) return value;
  return '#888888';
}

function composeShadow(s: ShadowState): string {
  const r = Math.round(parseInt(s.color.slice(1, 3), 16) || 0);
  const g = Math.round(parseInt(s.color.slice(3, 5), 16) || 0);
  const b = Math.round(parseInt(s.color.slice(5, 7), 16) || 0);
  return `${s.x}px ${s.y}px ${s.blur}px ${s.spread}px rgba(${r}, ${g}, ${b}, ${s.opacity})`;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function EditableColorSwatch({
  name,
  value,
  onChange,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/40 p-2.5">
      <div
        className="h-12 w-12 shrink-0 rounded-lg border border-white/40 cursor-pointer relative overflow-hidden"
        style={{ backgroundColor: value }}
      >
        <input
          type="color"
          value={toHexInput(value)}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
        />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate font-mono text-xs font-semibold" style={{ color: ADMIN_COLORS.navy }}>
          {name}
        </p>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full font-mono text-[10px] px-1.5 py-0.5 rounded border border-white/30 bg-white/50 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          style={{ color: ADMIN_COLORS.textTertiary }}
        />
      </div>
    </div>
  );
}

function ReadOnlyColorSwatch({ name, value }: { name: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/40 p-2.5">
      <div
        className="h-12 w-12 shrink-0 rounded-lg border border-white/40"
        style={{ backgroundColor: value }}
      />
      <div className="min-w-0">
        <p className="truncate font-mono text-xs font-semibold" style={{ color: ADMIN_COLORS.navy }}>
          {name}
        </p>
        <p className="truncate font-mono text-[10px]" style={{ color: ADMIN_COLORS.textTertiary }}>
          {value}
        </p>
        <span
          className="inline-block mt-0.5 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/50"
          style={{ color: ADMIN_COLORS.textTertiary }}
        >
          read-only (CSS var)
        </span>
      </div>
    </div>
  );
}

function EditableCssVarSwatch({
  varName,
  value,
  onChange,
}: {
  varName: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/40 p-2.5">
      <div
        className="h-12 w-12 shrink-0 rounded-lg border border-white/40 cursor-pointer relative overflow-hidden"
        style={{ backgroundColor: value.startsWith('#') || value.startsWith('rgb') ? value : `var(${varName})` }}
      >
        <input
          type="color"
          value={toHexInput(value)}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
        />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate font-mono text-xs font-semibold" style={{ color: ADMIN_COLORS.navy }}>
          {varName}
        </p>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full font-mono text-[10px] px-1.5 py-0.5 rounded border border-white/30 bg-white/50 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          style={{ color: ADMIN_COLORS.textTertiary }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

export function DesignTokensPage() {
  const { t } = useTranslation();

  /* ── Section 1: Color Palette state ─────────────────────────────── */
  const [colorValues, setColorValues] = useState<Record<string, string>>(
    () => ({ ...EDITABLE_COLOR_DEFAULTS }),
  );

  const updateColor = useCallback((name: string, value: string) => {
    setColorValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetColors = useCallback(() => {
    setColorValues({ ...EDITABLE_COLOR_DEFAULTS });
  }, []);

  /* ── Section 2: CSS Variables state ─────────────────────────────── */
  const [cssVarValues, setCssVarValues] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const group of CSS_VAR_GROUPS) {
      for (const step of group.steps) {
        const varName = `${group.prefix}-${step}`;
        map[varName] = getCssVarValue(varName);
      }
    }
    return map;
  });

  const updateCssVar = useCallback((varName: string, value: string) => {
    setCssVarValues((prev) => ({ ...prev, [varName]: value }));
    document.documentElement.style.setProperty(varName, value);
  }, []);

  const resetCssVars = useCallback(() => {
    const map: Record<string, string> = {};
    for (const group of CSS_VAR_GROUPS) {
      for (const step of group.steps) {
        const varName = `${group.prefix}-${step}`;
        document.documentElement.style.removeProperty(varName);
        map[varName] = getCssVarValue(varName);
      }
    }
    setCssVarValues(map);
  }, []);

  /* ── Section 3: Typography state ────────────────────────────────── */
  const [typo, setTypo] = useState<TypographyState>({
    fontFamily: 'Inter, sans-serif',
    headingSizes: [...DEFAULT_HEADING_SIZES],
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  });

  const updateTypo = useCallback(<K extends keyof TypographyState>(key: K, value: TypographyState[K]) => {
    setTypo((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateHeadingSize = useCallback((index: number, size: number) => {
    setTypo((prev) => {
      const next = [...prev.headingSizes];
      next[index] = size;
      return { ...prev, headingSizes: next };
    });
  }, []);

  const resetTypography = useCallback(() => {
    setTypo({
      fontFamily: 'Inter, sans-serif',
      headingSizes: [...DEFAULT_HEADING_SIZES],
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0,
    });
  }, []);

  /* ── Section 4: Shadows state ───────────────────────────────────── */
  const [shadows, setShadows] = useState<Record<string, ShadowState>>(
    () => JSON.parse(JSON.stringify(DEFAULT_SHADOW_STATES)),
  );

  const updateShadow = useCallback((key: string, field: keyof ShadowState, value: number | string) => {
    setShadows((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  }, []);

  const resetShadows = useCallback(() => {
    setShadows(JSON.parse(JSON.stringify(DEFAULT_SHADOW_STATES)));
  }, []);

  /* ── Section 5: Borders state ───────────────────────────────────── */
  const [borderState, setBorderState] = useState<BorderState>({
    width: 2,
    color: '#94a3b8',
    style: 'solid',
    radius: 12,
  });

  const updateBorder = useCallback(<K extends keyof BorderState>(key: K, value: BorderState[K]) => {
    setBorderState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetBorders = useCallback(() => {
    setBorderState({ width: 2, color: '#94a3b8', style: 'solid', radius: 12 });
  }, []);

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Page Header */}
      <PageHeader
        icon={<Paintbrush size={28} className="text-primary-600" />}
        title={t('designSystem.tokensTitle')}
        subtitle={t('designSystem.tokensSubtitle')}
      />

      {/* ================================================================= */}
      {/* Section 1: Color Palette                                          */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title="Color Palette"
          icon={<Palette className="h-4 w-4" />}
          headerAction={<ResetButton onClick={resetColors} label="Reset Colors" />}
        >
          <div className="space-y-6">
            {COLOR_CATEGORIES.map((category) => (
              <div key={category.label}>
                <SectionHeader>{category.label}</SectionHeader>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {category.tokens.map((token) => {
                    if (!token.editable) {
                      return (
                        <ReadOnlyColorSwatch
                          key={token.name}
                          name={token.name}
                          value={token.value}
                        />
                      );
                    }
                    return (
                      <EditableColorSwatch
                        key={token.name}
                        name={token.name}
                        value={colorValues[token.name] ?? token.value}
                        onChange={(v) => updateColor(token.name, v)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* Section 2: CSS Variables                                          */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title="CSS Variables"
          icon={<Code2 className="h-4 w-4" />}
          headerAction={<ResetButton onClick={resetCssVars} label="Reset Vars" />}
        >
          <div className="space-y-6">
            {CSS_VAR_GROUPS.map((group) => (
              <div key={group.prefix}>
                <SectionHeader>{group.prefix}</SectionHeader>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {group.steps.map((step) => {
                    const varName = `${group.prefix}-${step}`;
                    return (
                      <EditableCssVarSwatch
                        key={varName}
                        varName={varName}
                        value={cssVarValues[varName] || ''}
                        onChange={(v) => updateCssVar(varName, v)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* Section 3: Typography                                             */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title="Typography"
          icon={<Type className="h-4 w-4" />}
          headerAction={<ResetButton onClick={resetTypography} label="Reset Typography" />}
        >
          <div className="space-y-6">
            {/* Font Family */}
            <div>
              <SectionHeader>Font Family</SectionHeader>
              <ControlsPanel>
                <SelectControl
                  label="Family"
                  value={typo.fontFamily}
                  onChange={(v) => updateTypo('fontFamily', v)}
                  options={FONT_FAMILY_OPTIONS}
                />
              </ControlsPanel>
            </div>

            {/* Heading Sizes */}
            <div>
              <SectionHeader>Heading Sizes</SectionHeader>
              <div className="space-y-3">
                {HEADING_LEVELS.map((level, i) => (
                  <div key={level} className="flex items-center gap-4">
                    <SliderControl
                      label={level}
                      value={typo.headingSizes[i]}
                      onChange={(v) => updateHeadingSize(i, v)}
                      min={10}
                      max={48}
                      step={1}
                      unit="px"
                    />
                    <span
                      className="flex-1 truncate"
                      style={{
                        fontFamily: typo.fontFamily,
                        fontSize: `${typo.headingSizes[i]}px`,
                        fontWeight: typo.fontWeight,
                        lineHeight: typo.lineHeight,
                        letterSpacing: `${typo.letterSpacing}px`,
                        color: ADMIN_COLORS.navy,
                      }}
                    >
                      The quick brown fox
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Font Weight */}
            <div>
              <SectionHeader>Font Weight</SectionHeader>
              <ControlsPanel>
                <SliderControl
                  label="Weight"
                  value={typo.fontWeight}
                  onChange={(v) => updateTypo('fontWeight', v)}
                  min={100}
                  max={900}
                  step={100}
                />
              </ControlsPanel>
            </div>

            {/* Line Height */}
            <div>
              <SectionHeader>Line Height</SectionHeader>
              <ControlsPanel>
                <SliderControl
                  label="Line Height"
                  value={typo.lineHeight}
                  onChange={(v) => updateTypo('lineHeight', v)}
                  min={0.8}
                  max={2.5}
                  step={0.1}
                />
              </ControlsPanel>
            </div>

            {/* Letter Spacing */}
            <div>
              <SectionHeader>Letter Spacing</SectionHeader>
              <ControlsPanel>
                <SliderControl
                  label="Spacing"
                  value={typo.letterSpacing}
                  onChange={(v) => updateTypo('letterSpacing', v)}
                  min={-2}
                  max={4}
                  step={0.1}
                  unit="px"
                />
              </ControlsPanel>
            </div>

            {/* Live Preview */}
            <div>
              <SectionHeader>Live Preview</SectionHeader>
              <PreviewBox>
                <div
                  style={{
                    fontFamily: typo.fontFamily,
                    fontWeight: typo.fontWeight,
                    lineHeight: typo.lineHeight,
                    letterSpacing: `${typo.letterSpacing}px`,
                  }}
                >
                  {HEADING_LEVELS.map((level, i) => (
                    <p
                      key={level}
                      className="mb-2"
                      style={{
                        fontSize: `${typo.headingSizes[i]}px`,
                        color: ADMIN_COLORS.navy,
                      }}
                    >
                      {level} - The quick brown fox jumps over the lazy dog
                    </p>
                  ))}
                  <p
                    className="mt-4"
                    style={{
                      fontSize: '14px',
                      color: ADMIN_COLORS.textSecondary,
                    }}
                  >
                    Body text: Lorem ipsum dolor sit amet, consectetur adipiscing
                    elit. Sed do eiusmod tempor incididunt ut labore et dolore
                    magna aliqua. Ut enim ad minim veniam, quis nostrud
                    exercitation ullamco laboris nisi ut aliquip ex ea commodo
                    consequat.
                  </p>
                </div>
              </PreviewBox>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* Section 4: Shadows                                                */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title="Shadows"
          icon={<Layers className="h-4 w-4" />}
          headerAction={<ResetButton onClick={resetShadows} label="Reset Shadows" />}
        >
          <div className="space-y-8">
            {(['shadow', 'shadowMd', 'shadowLg'] as const).map((key) => {
              const s = shadows[key];
              const composed = composeShadow(s);
              return (
                <div key={key} className="space-y-4">
                  <SectionHeader>{key}</SectionHeader>

                  {/* Controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                    <SliderControl
                      label="X Offset"
                      value={s.x}
                      onChange={(v) => updateShadow(key, 'x', v)}
                      min={-20}
                      max={20}
                      step={1}
                      unit="px"
                    />
                    <SliderControl
                      label="Y Offset"
                      value={s.y}
                      onChange={(v) => updateShadow(key, 'y', v)}
                      min={-20}
                      max={20}
                      step={1}
                      unit="px"
                    />
                    <SliderControl
                      label="Blur"
                      value={s.blur}
                      onChange={(v) => updateShadow(key, 'blur', v)}
                      min={0}
                      max={60}
                      step={1}
                      unit="px"
                    />
                    <SliderControl
                      label="Spread"
                      value={s.spread}
                      onChange={(v) => updateShadow(key, 'spread', v)}
                      min={-10}
                      max={20}
                      step={1}
                      unit="px"
                    />
                    <ColorControl
                      label="Color"
                      value={s.color}
                      onChange={(v) => updateShadow(key, 'color', v)}
                    />
                    <SliderControl
                      label="Opacity"
                      value={s.opacity}
                      onChange={(v) => updateShadow(key, 'opacity', v)}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </div>

                  {/* Live Preview */}
                  <PreviewBox>
                    <div className="flex flex-col items-center gap-3">
                      <div
                        className="flex h-28 w-full max-w-sm items-center justify-center rounded-xl bg-white"
                        style={{ boxShadow: composed }}
                      >
                        <span
                          className="font-mono text-xs font-semibold"
                          style={{ color: ADMIN_COLORS.textSecondary }}
                        >
                          {key}
                        </span>
                      </div>
                      <p
                        className="max-w-full truncate font-mono text-[10px]"
                        style={{ color: ADMIN_COLORS.textTertiary }}
                      >
                        {composed}
                      </p>
                    </div>
                  </PreviewBox>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* Section 5: Borders                                                */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title="Borders"
          icon={<Square className="h-4 w-4" />}
          headerAction={<ResetButton onClick={resetBorders} label="Reset Borders" />}
        >
          <div className="space-y-6">
            {/* Controls */}
            <div>
              <SectionHeader>Controls</SectionHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                <SliderControl
                  label="Width"
                  value={borderState.width}
                  onChange={(v) => updateBorder('width', v)}
                  min={0}
                  max={5}
                  step={0.5}
                  unit="px"
                />
                <ColorControl
                  label="Color"
                  value={borderState.color}
                  onChange={(v) => updateBorder('color', v)}
                />
                <SelectControl
                  label="Style"
                  value={borderState.style}
                  onChange={(v) => updateBorder('style', v)}
                  options={BORDER_STYLE_OPTIONS}
                />
                <SliderControl
                  label="Radius"
                  value={borderState.radius}
                  onChange={(v) => updateBorder('radius', v)}
                  min={0}
                  max={32}
                  step={1}
                  unit="px"
                />
              </div>
            </div>

            {/* Live Preview */}
            <div>
              <SectionHeader>Live Preview</SectionHeader>
              <PreviewBox>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div
                    className="flex h-32 w-full items-center justify-center bg-white/50"
                    style={{
                      border: `${borderState.width}px ${borderState.style} ${borderState.color}`,
                      borderRadius: `${borderState.radius}px`,
                    }}
                  >
                    <div className="text-center">
                      <span
                        className="font-mono text-xs font-semibold block"
                        style={{ color: ADMIN_COLORS.textSecondary }}
                      >
                        Custom Border
                      </span>
                      <span
                        className="font-mono text-[10px] block mt-1"
                        style={{ color: ADMIN_COLORS.textTertiary }}
                      >
                        {borderState.width}px {borderState.style} {borderState.color}
                      </span>
                      <span
                        className="font-mono text-[10px] block"
                        style={{ color: ADMIN_COLORS.textTertiary }}
                      >
                        radius: {borderState.radius}px
                      </span>
                    </div>
                  </div>

                  {/* Compare with ADMIN_COLORS borders */}
                  <div className="space-y-4">
                    {([
                      { key: 'border', label: 'ADMIN_COLORS.border', value: colorValues.border ?? ADMIN_COLORS.border },
                      { key: 'borderMedium', label: 'ADMIN_COLORS.borderMedium', value: colorValues.borderMedium ?? ADMIN_COLORS.borderMedium },
                    ] as const).map(({ key, label, value }) => (
                      <div
                        key={key}
                        className="flex h-14 w-full items-center justify-center bg-white/50"
                        style={{
                          border: `${borderState.width}px ${borderState.style} ${value}`,
                          borderRadius: `${borderState.radius}px`,
                        }}
                      >
                        <span
                          className="font-mono text-[10px] font-semibold"
                          style={{ color: ADMIN_COLORS.textSecondary }}
                        >
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </PreviewBox>
            </div>
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}
